/**
 * MINSERT grips (FIN.7) — pure collector.
 *
 * Given an INSERT EntityPreview whose columnCount × rowCount exceed 1,
 * emit four resize grips:
 *
 *   - minsert-columns       — mid-right edge: drag horizontally → change columnCount
 *   - minsert-rows          — mid-bottom edge: drag vertically → change rowCount
 *   - minsert-col-spacing   — top-right corner: drag horizontally → change columnSpacing
 *   - minsert-row-spacing   — bottom-left corner: drag vertically → change rowSpacing
 *
 * Kept separate from the dynamic-block collector so `blockGripCollector.ts`
 * stays under the CLAUDE.md 1000-line budget. The API signature mirrors
 * `collectBlockGrips` so callers can `grips.push(...collectMinsertGrips(...))`.
 *
 * All coordinates are in WORLD space. The collector computes the grid
 * bounding box in the block's local coordinates (basePoint subtracted)
 * and passes every anchor through the shared INSERT matrix — supports
 * negative scale (mirror), rotation, and the block's own basePoint offset.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { BlockDefinition } from "@dxf-ai-editor/drawing-model";

import type { BlockGrip } from "./blockGripTypes";
import { composeBlockMatrix } from "./blockMatrix";
import { readNumber } from "./renderHelpers";

/**
 * Minimal grid geometry extracted from the INSERT preview + block
 * definition. Used by MINSERT grip collection.
 */
interface MinsertGridGeometry {
  readonly columnCount: number;
  readonly rowCount: number;
  readonly columnSpacing: number;
  readonly rowSpacing: number;
  /** Axis-aligned bounding box of the block content in local (author) coords. */
  readonly bboxMin: { x: number; y: number };
  readonly bboxMax: { x: number; y: number };
}

function readNumberProp(
  props: Record<string, string>,
  name: string,
  fallback: number,
): number {
  const raw = props[name];
  if (raw == null) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Walk BlockDefinition.entities and compute the AABB in local coords.
 * Non-trivial entity types (Text, Attdef, Block nested INSERT) contribute
 * their insertionPoint only — enough for MINSERT anchor placement, which
 * is a heuristic rather than an authoritative bounding box.
 *
 * Returns unit cell min/max with a 1×1 fallback when the block has no
 * extractable geometry (e.g. empty block).
 */
function computeBlockAabb(blockDef: BlockDefinition): {
  min: { x: number; y: number };
  max: { x: number; y: number };
} {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  const expand = (x: number, y: number) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };
  for (const entity of blockDef.entities) {
    switch (entity.type) {
      case "Line":
        expand(entity.start.x, entity.start.y);
        expand(entity.end.x, entity.end.y);
        break;
      case "Circle":
        expand(
          entity.center.x - entity.radius,
          entity.center.y - entity.radius,
        );
        expand(
          entity.center.x + entity.radius,
          entity.center.y + entity.radius,
        );
        break;
      case "Arc":
        expand(
          entity.center.x - entity.radius,
          entity.center.y - entity.radius,
        );
        expand(
          entity.center.x + entity.radius,
          entity.center.y + entity.radius,
        );
        break;
      case "LWPOLYLINE":
        for (const v of entity.vertices) expand(v.x, v.y);
        break;
      case "Text":
      case "MText":
      case "Attdef":
      case "Block":
        expand(entity.insertionPoint.x, entity.insertionPoint.y);
        break;
      default:
        // Other entity types contribute nothing — safe to skip.
        break;
    }
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
    // Empty block — 1×1 fallback so grips still appear at a usable spot.
    return { min: { x: 0, y: 0 }, max: { x: 1, y: 1 } };
  }
  return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
}

/**
 * Pull the live MINSERT grid geometry from the INSERT preview's property
 * bag. Returns `null` when the entity is a plain INSERT (1×1 grid) — no
 * grips to emit.
 */
export function readMinsertGrid(
  entity: EntityPreview,
  blockDef: BlockDefinition,
): MinsertGridGeometry | null {
  const columnCount = Math.max(
    1,
    Math.round(readNumberProp(entity.properties, "Column count", 1)),
  );
  const rowCount = Math.max(
    1,
    Math.round(readNumberProp(entity.properties, "Row count", 1)),
  );
  if (columnCount <= 1 && rowCount <= 1) return null;
  const columnSpacing = readNumberProp(entity.properties, "Column spacing", 0);
  const rowSpacing = readNumberProp(entity.properties, "Row spacing", 0);
  const aabb = computeBlockAabb(blockDef);
  return {
    columnCount,
    rowCount,
    columnSpacing,
    rowSpacing,
    bboxMin: aabb.min,
    bboxMax: aabb.max,
  };
}

/**
 * Emit the four MINSERT grips for an INSERT preview. Returns empty
 * array when the entity is a plain 1×1 INSERT. The grips use the
 * shared INSERT matrix so rotation, scale, mirror and basePoint are
 * all handled uniformly.
 */
export function collectMinsertGrips(
  entity: EntityPreview,
  blockDef: BlockDefinition,
): BlockGrip[] {
  const grid = readMinsertGrid(entity, blockDef);
  if (!grid) return [];

  // Re-derive the INSERT matrix (mirrors readInsertMatrix in
  // blockGripCollector, but kept local so this module is stand-alone).
  const props = entity.properties;
  let ix = Number(props["Insertion X"]);
  let iy = Number(props["Insertion Y"]);
  if (!Number.isFinite(ix)) ix = 0;
  if (!Number.isFinite(iy)) iy = 0;
  let sx = Number(props["Scale X"]);
  let sy = Number(props["Scale Y"]);
  if (!Number.isFinite(sx) || Math.abs(sx) < 1e-9) sx = 1;
  if (!Number.isFinite(sy) || Math.abs(sy) < 1e-9) sy = 1;
  const matrix = composeBlockMatrix({
    translate: { x: ix, y: iy },
    rotateDeg: readNumber(props, "Rotation", 0),
    scaleX: sx,
    scaleY: sy,
    basePoint: blockDef.basePoint,
  });

  // Cell dimensions = bbox extent + one spacing. We use the max corner
  // so the grid reads left-to-right, top-to-bottom like AutoCAD.
  const cellWidth = grid.bboxMax.x - grid.bboxMin.x;
  const cellHeight = grid.bboxMax.y - grid.bboxMin.y;
  const stepX = cellWidth + grid.columnSpacing;
  const stepY = cellHeight + grid.rowSpacing;

  // Grid corners in LOCAL author-space. The grid's "(0,0)" cell lives
  // at bboxMin (the block's own bbox). Column i lives at x = i*stepX,
  // row j lives at y = j*stepY offset from bbox.
  const lastCol = grid.columnCount - 1;
  const lastRow = grid.rowCount - 1;

  // Grip anchors (author-space). We place them at mid-edge / corner
  // spots relative to the overall grid — intuitive for dragging.
  const topLeftAuthor = {
    x: grid.bboxMin.x + lastCol * stepX + cellWidth,
    y: grid.bboxMin.y, // row 0
  };
  const bottomLeftAuthor = {
    x: grid.bboxMin.x,
    y: grid.bboxMin.y + lastRow * stepY + cellHeight,
  };
  const midRightAuthor = {
    x: grid.bboxMin.x + lastCol * stepX + cellWidth,
    y: grid.bboxMin.y + (lastRow * stepY + cellHeight) / 2,
  };
  const midBottomAuthor = {
    x: grid.bboxMin.x + (lastCol * stepX + cellWidth) / 2,
    y: grid.bboxMin.y + lastRow * stepY + cellHeight,
  };

  const colSpacingWorld = matrix.transformPoint(topLeftAuthor);
  const rowSpacingWorld = matrix.transformPoint(bottomLeftAuthor);
  const colCountWorld = matrix.transformPoint(midRightAuthor);
  const rowCountWorld = matrix.transformPoint(midBottomAuthor);

  // Compute per-axis world direction by transforming a unit step along
  // author X/Y axes — preserves rotation and negative-scale mirror so
  // drag projection in the shell can reuse `linear-second` math.
  const originWorld = matrix.transformPoint({ x: 0, y: 0 });
  const xAxisTipWorld = matrix.transformPoint({ x: 1, y: 0 });
  const yAxisTipWorld = matrix.transformPoint({ x: 0, y: 1 });
  const xAngleRad = Math.atan2(
    xAxisTipWorld.y - originWorld.y,
    xAxisTipWorld.x - originWorld.x,
  );
  const yAngleRad = Math.atan2(
    yAxisTipWorld.y - originWorld.y,
    yAxisTipWorld.x - originWorld.x,
  );

  return [
    {
      id: `minsert-${entity.id}-cols`,
      kind: "minsert-columns",
      entityId: entity.id,
      parameterId: "__minsert_cols__",
      parameterName: "Columns",
      x: colCountWorld.x,
      y: colCountWorld.y,
      cursor: "move",
      axisAngleRad: xAngleRad,
      axisBaseWorld: colCountWorld,
      minsertColumnCount: grid.columnCount,
      minsertRowCount: grid.rowCount,
      minsertColumnSpacing: grid.columnSpacing,
      minsertRowSpacing: grid.rowSpacing,
    },
    {
      id: `minsert-${entity.id}-rows`,
      kind: "minsert-rows",
      entityId: entity.id,
      parameterId: "__minsert_rows__",
      parameterName: "Rows",
      x: rowCountWorld.x,
      y: rowCountWorld.y,
      cursor: "move",
      axisAngleRad: yAngleRad,
      axisBaseWorld: rowCountWorld,
      minsertColumnCount: grid.columnCount,
      minsertRowCount: grid.rowCount,
      minsertColumnSpacing: grid.columnSpacing,
      minsertRowSpacing: grid.rowSpacing,
    },
    {
      id: `minsert-${entity.id}-col-spacing`,
      kind: "minsert-col-spacing",
      entityId: entity.id,
      parameterId: "__minsert_col_spacing__",
      parameterName: "Column spacing",
      x: colSpacingWorld.x,
      y: colSpacingWorld.y,
      cursor: "move",
      axisAngleRad: xAngleRad,
      axisBaseWorld: colSpacingWorld,
      minsertColumnCount: grid.columnCount,
      minsertRowCount: grid.rowCount,
      minsertColumnSpacing: grid.columnSpacing,
      minsertRowSpacing: grid.rowSpacing,
    },
    {
      id: `minsert-${entity.id}-row-spacing`,
      kind: "minsert-row-spacing",
      entityId: entity.id,
      parameterId: "__minsert_row_spacing__",
      parameterName: "Row spacing",
      x: rowSpacingWorld.x,
      y: rowSpacingWorld.y,
      cursor: "move",
      axisAngleRad: yAngleRad,
      axisBaseWorld: rowSpacingWorld,
      minsertColumnCount: grid.columnCount,
      minsertRowCount: grid.rowCount,
      minsertColumnSpacing: grid.columnSpacing,
      minsertRowSpacing: grid.rowSpacing,
    },
  ];
}

/**
 * Compute cellWidth / cellHeight in author-space for a given block def.
 * Exposed so the shell drag handler doesn't need to re-walk the block
 * definition — called once at drag start.
 */
export function readBlockCellSize(blockDef: BlockDefinition): {
  cellWidth: number;
  cellHeight: number;
} {
  const aabb = computeBlockAabb(blockDef);
  return {
    cellWidth: aabb.max.x - aabb.min.x,
    cellHeight: aabb.max.y - aabb.min.y,
  };
}

/**
 * Given the original grip (which captures columnCount/rowCount at drag
 * start) and the cumulative mouse delta in local author-space pixels,
 * return the {count, spacing} value set the drag should commit.
 *
 * Drag semantics:
 *   - minsert-columns     — add one column per `cellWidth+columnSpacing` delta; min=1
 *   - minsert-rows        — add one row per `cellHeight+rowSpacing` delta; min=1
 *   - minsert-col-spacing — spacing = initial + dx; min=0
 *   - minsert-row-spacing — spacing = initial + dy; min=0
 */
export function resolveMinsertDragValue(
  grip: BlockGrip,
  deltaAuthorX: number,
  deltaAuthorY: number,
  blockCellWidth: number,
  blockCellHeight: number,
): { columnCount?: number; rowCount?: number; columnSpacing?: number; rowSpacing?: number } {
  if (grip.kind === "minsert-columns") {
    const initial = grip.minsertColumnCount ?? 1;
    const spacing = grip.minsertColumnSpacing ?? 0;
    const step = Math.max(1e-6, blockCellWidth + spacing);
    const added = Math.round(deltaAuthorX / step);
    return { columnCount: Math.max(1, initial + added) };
  }
  if (grip.kind === "minsert-rows") {
    const initial = grip.minsertRowCount ?? 1;
    const spacing = grip.minsertRowSpacing ?? 0;
    const step = Math.max(1e-6, blockCellHeight + spacing);
    const added = Math.round(deltaAuthorY / step);
    return { rowCount: Math.max(1, initial + added) };
  }
  if (grip.kind === "minsert-col-spacing") {
    const initial = grip.minsertColumnSpacing ?? 0;
    return { columnSpacing: Math.max(0, initial + deltaAuthorX) };
  }
  if (grip.kind === "minsert-row-spacing") {
    const initial = grip.minsertRowSpacing ?? 0;
    return { rowSpacing: Math.max(0, initial + deltaAuthorY) };
  }
  return {};
}
